import { usePermission } from '@backstage/plugin-permission-react';
import { catalogUserUpdatePermission } from '../users/permissions';
import { Fragment, ReactNode, useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
  useRouteRefParams,
} from '@backstage/core-plugin-api';
import { entityRouteRef } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';

// Populated before the catalog page is mounted; the server is the authorization authority.
export const collaboratorAccess = new Map<string, boolean>();
export const auditAccess = new Map<string, boolean>();
export const environmentAccess = new Map<string, boolean>();
export function EnvironmentAccessGate({ children }: { children: ReactNode }) {
  const permission = usePermission({ permission: catalogUserUpdatePermission });
  const params = useRouteRefParams(entityRouteRef);
  const ref = stringifyEntityRef(params);
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [loaded, setLoaded] = useState('');
  const [projectAdmin, setProjectAdmin] = useState(false);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let active = true;
    setProjectAdmin(false);
    auditAccess.delete(ref);
    environmentAccess.delete(ref);
    const load = async () => {
      let allowed = false;
      try {
        const base = await discovery.getBaseUrl('template-provisioning');
        const response = await fetchApi.fetch(
          `${base}/environments?componentRef=${encodeURIComponent(ref)}`,
        );
        if (response.ok) {
          const data = await response.json();
          allowed = data.allowed === true;
          if (active) {
            setProjectAdmin(data.projectAdmin === true);
            auditAccess.set(ref, data.auditAllowed === true);
          }
        }
      } catch {
        /* Hide privileged tabs if access cannot be checked. */
      }
      if (active) {
        const changed = environmentAccess.get(ref) !== allowed;
        environmentAccess.set(ref, allowed);
        setLoaded(ref);
        if (changed) setVersion(value => value + 1);
      }
    };
    load();
    return () => {
      active = false;
      auditAccess.delete(ref);
      environmentAccess.delete(ref);
      collaboratorAccess.delete(ref);
    };
  }, [ref, discovery, fetchApi]);
  collaboratorAccess.set(ref, permission.allowed || (loaded === ref && projectAdmin));
  return loaded === ref && !permission.loading ? (
    <Fragment key={`${ref}:${version}`}>{children}</Fragment>
  ) : null;
}
